import { meContract } from '@mntn/api-contracts/flight-deck/v1/me'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { buildFlightDeckTsRestClient } from '@/flight-deck-api/ts-rest'

export const FLIGHT_DECK_SESSION_QUERY_KEY = ['flight-deck-session']
const SESSION_STALE_TIME_MS = 5 * 60 * 1000
const SESSION_REFRESH_INTERVAL_MS = 50 * 60 * 1000

const fetchSession = async (meClient: ReturnType<typeof buildFlightDeckTsRestClient<typeof meContract>>) => {
  const response = await meClient.get()

  if (response.status === 401) return null

  if (response.status !== 200) {
    throw new Error(`Failed to load session from /v1/me: ${response.status}`)
  }

  const { data } = response.body

  if (data.tenant.kind !== 'advertiser') return null

  const { advertiserId, companyName, logo } = data.tenant.current
  return {
    loginAs: data.tenant.loginAs,
    cloudinaryAccessToken: data.cloudinaryAccessToken,
    cloudinaryAccessTokenExpiresAt: data.cloudinaryAccessTokenExpiresAt,
    advertiser: {
      id: advertiserId,
      name: companyName,
      logo: logo ?? undefined,
    },
  }
}

export const useFlightDeckSession = ({
  getToken,
  enabled,
}: {
  getToken: () => Promise<string | null>
  enabled: boolean
}) => {
  const meClient = useMemo(() => buildFlightDeckTsRestClient(meContract, getToken), [getToken])

  const {
    data: session = null,
    isLoading,
    error,
  } = useQuery({
    queryKey: FLIGHT_DECK_SESSION_QUERY_KEY,
    queryFn: () => fetchSession(meClient),
    enabled,
    staleTime: SESSION_STALE_TIME_MS,
    refetchInterval: SESSION_REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  })

  return { session, isLoading, error }
}
